import React, { useState, useEffect } from 'react';
import { Modal, Button, Spinner, Alert, Form } from 'react-bootstrap';

import coldemailer_api from '../coldemailer_api';

const c_TouchNumbers = [1, 2, 3];

// Shows one touch as a recipient would receive it: the rendered message first, then the two parts
// the message is actually made of. Nothing here sends anything or writes anything.
function PreviewEmailModal({ show, on_close }) {

	const [preview, set_preview] = useState(null);
	const [loading, set_loading] = useState(false);
	const [error, set_error] = useState('');
	const [touch_number, set_touch_number] = useState(1);

	const load_preview = async (touch) => {
		set_loading(true);
		set_error('');

		try {
			const data = await coldemailer_api.preview_email(null, null, touch);
			set_preview(data);
		}
		catch (error) {
			console.log('PreviewEmailModal/load_preview - ERROR: ', error);

			// The reason, not a stand-in for it: a preview that fails silently is one nobody can
			// act on. The server's own wording is shown wherever it sends one.
			const response_data = error.response ? error.response.data : null;
			const server_message = response_data ? (response_data.error ? response_data.error : (response_data.message ? response_data.message : null)) : null;

			set_error(server_message ? server_message : error.message);
			set_preview(null);
		}

		set_loading(false);
	};

	useEffect(() => {
		if(show) {
			load_preview(touch_number);
		}
	}, [show, touch_number]);

	const on_regenerate = () => {
		load_preview(touch_number);
	};

	const on_touch_change = (event) => {
		set_touch_number(parseInt(event.target.value, 10));
	};

	const render_loading = () => {
		return (
			<div className="ce-preview-loading">
				<Spinner animation="border" size="sm" />
				<span className="ce-preview-loading-text">Building the message...</span>
			</div>
		);
	};

	const render_error = () => {
		return (
			<Alert variant="danger">{error}</Alert>
		);
	};

	const render_summary = () => {
		return (
			<div className="ce-preview-summary">
				<div><span className="ce-preview-label">To</span>{preview.recipient_email}</div>
				<div><span className="ce-preview-label">Subject</span>{preview.subject}</div>
				<div><span className="ce-preview-label">Company</span>{preview.company_name}</div>
				<div><span className="ce-preview-label">Structure</span>{preview.structure}</div>
			</div>
		);
	};

	const render_rendered = () => {
		return (
			<React.Fragment>
				<div className="ce-preview-heading">As the recipient sees it</div>
				<div className="ce-preview-rendered" dangerouslySetInnerHTML={{ __html: preview.html_part }} />
			</React.Fragment>
		);
	};

	const render_parts = () => {
		return (
			<React.Fragment>
				<div className="ce-preview-heading">text/plain</div>
				<pre className="ce-preview-source">{preview.text_part}</pre>

				<div className="ce-preview-heading">text/html</div>
				<pre className="ce-preview-source">{preview.html_part}</pre>
			</React.Fragment>
		);
	};

	const render_body = () => {
		if(loading) {
			return render_loading();
		}

		if(error) {
			return render_error();
		}

		if(!preview) {
			return null;
		}

		return (
			<React.Fragment>
				{render_summary()}
				{render_rendered()}
				{render_parts()}
			</React.Fragment>
		);
	};

	return (
		<Modal show={show} onHide={on_close} size="lg" centered scrollable>
			<Modal.Header closeButton>
				<Modal.Title>Test Email</Modal.Title>
			</Modal.Header>

			<Modal.Body>
				<Form.Group className="ce-preview-touch">
					<Form.Label>Touch</Form.Label>
					<Form.Select value={touch_number} onChange={on_touch_change}>
						<option value={0}>Any</option>
						{c_TouchNumbers.map((touch) => {
							return (
								<option key={touch} value={touch}>Touch {touch}</option>
							);
						})}
					</Form.Select>
				</Form.Group>

				{render_body()}
			</Modal.Body>

			<Modal.Footer>
				<Button variant="outline-light" size="sm" className="ce-button" onClick={on_regenerate} disabled={loading}>
					Another Company
				</Button>

				<Button variant="outline-light" size="sm" className="ce-button" onClick={on_close}>
					Close
				</Button>
			</Modal.Footer>
		</Modal>
	);
}

export default PreviewEmailModal;
